/**
 * Chart Data Extractors
 * Functions for extracting and transforming data for performance charts
 */

import { formatNum } from '@/util/number';
import { kmhToKnot } from '@/util/units';
import { formatDuration } from '@/views/voyage/composables/useVoyageFormatters';

// headSeas → Head Seas
export function formatDirectionName(key) {
  const spaced = key.replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Extract RPM series data from raw RPM data
 * @param {Object} rpmData - Raw RPM data from API
 * @returns {Array} Array of RPM data points sorted by timestamp
 */
export function extractRpmSeries(rpmData) {
  if (!rpmData || !rpmData.data) return [];

  return Object.entries(rpmData.data)
    .map(([timestamp, data]) => ({
      timestamp: parseInt(timestamp),
      datetime: data.PORT?.datetime || data.STARBOARD?.datetime || '',
      portRpm: data.PORT?.rpm || 0,
      starboardRpm: data.STARBOARD?.rpm || 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Extract flowmeter (fuel consumption) series data from raw flowmeter data
 * @param {Object} flowmeterData - Raw flowmeter data from API
 * @returns {Array} Array of fuel consumption data points sorted by timestamp
 */
export function extractFlowmeterSeries(flowmeterData) {
  if (!flowmeterData || !flowmeterData.data) return [];

  return Object.entries(flowmeterData.data)
    .map(([timestamp, data]) => ({
      timestamp: parseInt(timestamp),
      datetime: data.PORT_IN?.datetime || '',
      totalFuelCons: data.ME_FuelCons || 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Extract GPS series data (speed and wave height) from raw GPS data
 * @param {Object} gpsData - Raw GPS data from API
 * @returns {Array} Array of GPS data points sorted by timestamp
 */
export function extractGpsSeries(gpsData) {
  if (!gpsData || !gpsData.data) return [];

  return Object.entries(gpsData.data)
    .map(([timestamp, data]) => ({
      timestamp: parseInt(timestamp),
      datetime: data.datetime || '',
      speed: data.speed ? kmhToKnot(data.speed) : 0,
      waveHeight: data.waveHeight || 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Prepare operation mode table items
 * @param {Object} engineData - Engine operation mode data
 * @returns {Array} Array of formatted table items
 */
export function getOperationModeItems(engineData) {
  if (!engineData) return [];

  const totalRunningSeconds = Object.values(engineData).reduce(
    (sum, data) => sum + (data.runningSeconds || 0),
    0,
  );

  return Object.entries(engineData).map(([modeName, data]) => {
    const percentage = totalRunningSeconds > 0
      ? formatNum((((data.runningSeconds || 0) * 100) / totalRunningSeconds), 2)
      : 0;
    return {
      modeName,
      percentage: `${percentage}%`,
      runningTime: formatDuration(data.runningSeconds),
    };
  });
}

/**
 * Prepare wave direction duration table items
 * @param {Object} weatherMetrics - Weather metrics from summary
 * @returns {Array} Array of formatted table items
 */
export function getWaveDurationItems(weatherMetrics) {
  if (!weatherMetrics?.runningSecondsDirection) return [];

  const runningSecondsDirection = weatherMetrics.runningSecondsDirection;
  const totalRunningSeconds = Object.values(runningSecondsDirection).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );

  return Object.entries(runningSecondsDirection).map(([directionName, seconds]) => {
    const percentage = totalRunningSeconds > 0
      ? formatNum((((seconds || 0) * 100) / totalRunningSeconds), 2)
      : 0;
    return {
      directionName: formatDirectionName(directionName),
      percentage: `${percentage}%`,
      duration: formatDuration(seconds),
    };
  });
}

/**
 * Prepare main engine detail items (Fuel Consumption + Running Time)
 * Key fuel huruf kecil (port), key running huruf besar (PORT) — digabung
 * case-insensitive. Kapal AE-only cuma punya sisi fuel-nya.
 * @param {Object} mainEngineMetrics - Main engine metrics from summary
 * @returns {Array} Array of formatted table items
 */
export function getMainEngineItems(mainEngineMetrics) {
  const toUpperMap = (source) => new Map(
    Object.entries(source || {}).map(([name, value]) => [name.toUpperCase(), value]),
  );

  const fuel = toUpperMap(mainEngineMetrics?.fuelConsEngine);
  const running = toUpperMap(mainEngineMetrics?.totalRunningSeconds);

  return [...new Set([...fuel.keys(), ...running.keys()])].map((name) => ({
    name,
    fuel: fuel.has(name) ? `${formatNum(Number(fuel.get(name) || 0), 2)} L` : '—',
    running: running.has(name) ? formatDuration(running.get(name) || 0) : '—',
  }));
}

/**
 * Prepare combined AE metrics table items (Running Hours + Fuel Consumption)
 * @param {Object} aeMetrics - AE metrics from summary
 * @returns {Array} Array of formatted table items with combined data
 */
export function getAeCombinedItems(aeMetrics) {
  if (!aeMetrics) return [];

  const runningSeconds = aeMetrics.runningSeconds || {};
  const fuelConsumption = aeMetrics.fuelConsumption || {};

  // Get all unique engine names from both data sources
  const allEngines = new Set([
    ...Object.keys(runningSeconds),
    ...Object.keys(fuelConsumption),
  ]);

  // Calculate totals
  const totalRunningSeconds = Object.values(runningSeconds).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );
  const totalFuel = Object.values(fuelConsumption).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );

  return Array.from(allEngines).map((engineName) => {
    const seconds = runningSeconds[engineName] || 0;
    const fuel = fuelConsumption[engineName] || 0;

    const runningPercentage = totalRunningSeconds > 0
      ? formatNum((((seconds || 0) * 100) / totalRunningSeconds), 2)
      : 0;
    const fuelPercentage = totalFuel > 0
      ? formatNum((((fuel || 0) * 100) / totalFuel), 2)
      : 0;

    return {
      engineName,
      runningTime: formatDuration(seconds),
      runningPercentage: `${runningPercentage}%`,
      fuelConsumption: `${formatNum(Number(fuel), 2)} L`,
      fuelPercentage: `${fuelPercentage}%`,
    };
  });
}
